/**
 * The one place a machine word becomes a sentence a person would say.
 *
 * Every Owner page reads its content out of the ledger, and the ledger speaks
 * in tool keys, category words, effect levels and provider error strings. None
 * of those may reach an Owner's eyes: an owner who has never read the code has
 * to be able to read the page. So each of them is looked up here, once, and a
 * lookup that misses says so out loud rather than falling through to the raw
 * word -- a page that prints `memory_timeline.fact_write` because a dictionary
 * had a hole is exactly the failure this module exists to prevent.
 *
 * What this module is not: it does not fetch, does not draw, and does not
 * decide what a card means. It turns one machine word into one Chinese phrase.
 * The folds that decide which rows become which card live in `project.ts`, and
 * the folds that assemble a whole question live in `ask.ts`.
 *
 * `unknownWords` is the escape valve, and it is deliberate. A caller that meets
 * a word this file has never heard of gets `null`, not the word, and is
 * expected to draw the honest sentence for "this installation has something in
 * it that the Studio cannot yet name" -- which is also the finding that says
 * which plugin has to supply a label.
 */

import type { DeclaredFailure } from "../lib/api";

/** What a person is being asked, by the category the asking policy filed. */
/**
 * `ToolSpec.effect` -- how much of the world a call touches, as the default
 * plugin set names it (`plugins/vocab/effects.py` `EffectLevel`).
 */
const REACH_WORDS: Record<string, string> = {
  read: "只是看一眼，不动任何东西",
  write: "会改它自己存着的东西",
  external: "会对外面动手",
  paid: "会花钱",
};

/**
 * `ToolSpec.effect_class` -- what kind of thing it is
 * (`plugins/vocab/effects.py` `EffectClass`).
 */
const KIND_WORDS: Record<string, string> = {
  memory: "记忆",
  message: "发消息",
  file: "文件",
  file_write: "写入文件",
  external_send: "向外部发送内容",
  script_execution: "执行程序或脚本",
  destructive: "删除等破坏性操作",
  network_read: "读取网络内容",
  authority_change: "变更权限",
  network: "上网",
  process: "在这台电脑上运行程序",
  payment: "付款",
  identity: "身份",
  other: "",
  unknown: "",
};

/**
 * Which failure this was, when the failing side said so.
 *
 * It used to be a guess. Five English regexes read a provider's message and the
 * page printed whichever matched, and a failure none of them matched read
 * 「暂未确认失败原因」 (D372). The walk then found the same hole one layer out: a run
 * failed with 「缺口：本 Producer 缺 TTS_API_KEY」 in its own `error`, which says
 * exactly what is missing, and the overview said it could not tell why (D661,
 * z08c-relogin-overview).
 *
 * So the guessing is gone rather than extended. What is rendered is the sentence
 * whoever failed wrote for the Owner (`DeclaredFailure`). Anything else says it
 * does not know, which is the finding that says who owes a sentence.
 */
const UNSAID = "暂未确认失败原因，请打开原对话查看记录。";

/**
 * Shapes a whole value takes when it is a name the machine gave something.
 *
 * Narrower than the Owner vocabulary gate on purpose, and a different question:
 * the gate asks 「is there an internal word anywhere in this text」, which is
 * right for text this shell wrote; this asks 「is this value a name rather than
 * prose」, which is the only question a page can ask about a value somebody else
 * supplied. A sentence that happens to contain a tool key is still that
 * person's sentence and is shown as they wrote it; a value that *is* the key is
 * the machine's and is not shown at all.
 *
 * `src/studio/ask.vocab.test.ts` checks the one-way relation that matters: every
 * value this calls a name is also one `scripts/owner-vocab.mjs` refuses, so the
 * page cannot quietly admit something the gate would fail.
 */
const MACHINE_SHAPES: RegExp[] = [
  /** JSON, whatever it is JSON of. */
  /[{}[\]]["']|["']\s*:/,
  /** `a.b` -- a tool or panel key, or a reference built like one. */
  /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/,
  /**
   * One identifier: letters, digits and underscores and nothing else.
   *
   * Leading and trailing underscores included, because `__probe_local_root__`
   * is as much a name as `source_ref` is, and a single bare Latin word
   * (`private`, `facts`) is one too -- no sentence in this product is one word
   * of Latin. A value that is only digits is excluded before this is asked: a
   * number is a number, and the gate skips those as well.
   */
  /^_*[A-Za-z0-9]+(_+[A-Za-z0-9]+)*_*$/,
  /**
   * `layer='fact'` -- a name, an operator, and a value: an expression written
   * for a query engine. The owner's card carried one of these (D431).
   */
  /^[A-Za-z_][A-Za-z0-9_.]*\s*[=<>!]=?/,
  /** `eff-…`, `turn_…` -- an id, whatever it identifies. */
  /^(eff|hdl|turn|obl|prn|call|run|msg|s|m|gen|APR|apr)[-_]/,
  /** 16+ hex -- a hash. */
  /^[0-9a-f]{16,}$/i,
];

/** A number, which is a number whatever it counts. */
const JUST_A_NUMBER = /^\d+([.,]\d+)*$/;

/** At least one Han character: the test for "somebody could read this aloud". */
const HAN = /[\u3400-\u4DBF\u4E00-\u9FFF]/;

/**
 * A path, wherever it points.
 *
 * `C:\pa-rw\hub\agents\ag_…\runs\ip-001-custom-cabinet` sat on a payment card
 * under a readable label, because a path is not JSON, not an identifier and not
 * an expression: it matched none of the shapes above and was printed as if it
 * were words (D662). A separator with a Latin segment beside it is the test, so
 * 「读/写」 stays a pair of Chinese words rather than becoming a directory.
 */
/** A web address, which is a place an owner may need to see and not a path. */
const URL_SHAPE = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//;

const PATH_SHAPE = /^(?:[A-Za-z]:[\u005C/]|[\u005C]{2}|\/)|[\u005C/][A-Za-z0-9._~-]+(?:[\u005C/]|$)/;

/** A file's own name at the end of a path: `…/render/final.mp4` -> `final.mp4`. */
const FILE_NAME = /^[A-Za-z0-9\u3400-\u4DBF\u4E00-\u9FFF][^\u005C/:*?"<>|\s]*\.[A-Za-z0-9]{1,8}$/;

/**
 * Currency codes, as the unit of the amount beside them.
 *
 * A code is an identifier and was dropped as one, so the card carried a number
 * with nothing to say what it counted -- 「预计最多花多少 0」, no currency anywhere
 * on a payment card (D663). A code this table has never heard of stays dropped:
 * an amount in a currency nobody can name is not made readable by printing it.
 */
const MONEY_WORDS: Record<string, string> = {
  CNY: "元",
  RMB: "元",
  USD: "美元",
  EUR: "欧元",
  JPY: "日元",
  HKD: "港元",
  TWD: "新台币",
  GBP: "英镑",
  KRW: "韩元",
};

/**
 * Is this value a machine's name for something, rather than words?
 *
 * The page prints an argument's value next to the Chinese label the tool's own
 * schema gave it, and used to print whatever the value was: `source_ref` is
 * described in Chinese and its value is a tool key, so an internal key sat
 * on the owner's card under a readable label (D431).
 */
export function machineValue(value: string): boolean {
  const said = value.trim();
  if (!said) return true;
  if (JUST_A_NUMBER.test(said)) return false;
  if (URL_SHAPE.test(said)) return false;
  if (PATH_SHAPE.test(said)) return true;
  return MACHINE_SHAPES.some((shape) => shape.test(said));
}

/**
 * The file this value names, or "" when it does not name one.
 *
 * A path is not something to print, but the file at the end of one is: what the
 * Owner is being asked about is 「final.mp4」, and the directories above it are
 * this system's own bookkeeping. A last segment with no extension is not a file
 * -- `…\runs\ip-001-custom-cabinet` is a run's id -- and is left to be counted
 * with everything else the card could not say.
 */
export function fileName(value: string): string {
  const said = value.trim();
  // A separator is required: a tool key is an identifier that happens to be
  // shaped like `name.ext`, and promoting it would undo D431.
  if (!/[\u005C/]/.test(said)) return "";
  const last = said.split(/[\u005C/]+/).filter(Boolean).at(-1) ?? "";
  return FILE_NAME.test(last) ? last : "";
}

/** The unit for an amount, or "" when nothing here names this currency. */
export function moneyWords(code: string): string {
  return MONEY_WORDS[code.trim().toUpperCase()] ?? "";
}

const CHANNEL_WORDS: Record<string, string> = {
  workbench: "涟漪网页",
};

/** What to call this channel to a person, or "" to use its own title. */
export function channelWords(key: string, labels: Record<string, string> = {}): string {
  return CHANNEL_WORDS[key] ?? labels[key] ?? "";
}

/**
 * Where a person can be reached, in words.  A seat is `channel:account`; the account is an id
 * nobody reads (and one channel can hold several for its own reasons), so only the channel is
 * named, once.
 */
export function seatWords(seats: string[] | undefined, labels: Record<string, string> = {}): string[] {
  const names = (seats ?? []).map(seat => channelWords(seat.split(":", 1)[0], labels) || "其他渠道");
  return [...new Set(names)];
}

/** What a person is being asked, or null when nothing in this tree names it. */


/** How far this call reaches, or null when the tool never declared. */
export function reachWords(level: string): string | null {
  return REACH_WORDS[level] ?? null;
}

/** What kind of thing this is, or null when the tool said `other` or nothing. */
export function kindWords(effectClass: string): string | null {
  return KIND_WORDS[effectClass] || null;
}

/**
 * What went wrong, in one sentence a person can act on.
 *
 * Never empty, and never this shell's own reading of somebody else's prose: the
 * declared sentence first, the core's published failure second, and otherwise
 * the admission, which points back at the record.
 */
export function failureWords(_raw: string, declared?: DeclaredFailure | null): string {
  if (declared?.sentence) return declared.sentence;
  return UNSAID;
}

/**
 * The first clause of a sentence a plugin wrote for the owner, as a label.
 *
 * Cut at the first terminator so a three-line help text does not become a
 * heading: 「改完之后，这一页会在下一轮生效」 becomes 「改完之后」. An opening
 * bracket is a terminator too -- 「写入长期记忆（由记忆管线的对账结果提交，或由
 * 模型直接调用）」 cut at the comma leaves a clause that opens a parenthesis and
 * never closes it, which is worse than the shorter true one. Returns "" when
 * there is nothing to cut, and when what comes out has no Han character in it,
 * because a label nobody can read aloud is not a label.
 *
 * It used to be asked of a `ToolSpec.description` and of a parameter's, and it
 * used to put the clause to `readableToOwner` before returning it. Both are
 * gone (§7.183). A description is written for the model, so no cut of it is an
 * owner's sentence and the shell does not print one: the tool declares
 * `owner.act` and `owner.params`, or the card says the category's act and
 * counts what it could not name. And the filter was the shell covering for a
 * plugin: a clause that failed the word list became `""` silently, so the owner
 * could not see that something was missing and the plugin's author never found
 * out their sentence had been thrown away (D780 -- and the filter was a
 * render-time judgement about somebody else's words, which is what the contract
 * replaces).
 *
 * What is left is one caller: the sentence a plugin writes under a
 * configuration setting (`ConfigField.description`, which
 * the settings plugin puts on the form as that box's
 * `placeholder`). That one is already written for the owner and already drawn
 * on their settings page; the gate checks it there, as a declaration
 * (`scripts/owner-vocab.mjs`).
 */
export function firstClause(description: string, limit = 24): string {
  const text = (description || "").trim();
  if (!text) return "";
  const cut = text.search(/[。；：，,.;:\n（(\[【]/);
  const head = (cut > 0 ? text.slice(0, cut) : text).trim();
  const said = head.length > limit ? `${head.slice(0, limit)}…` : head;
  return HAN.test(said) ? said : "";
}

/**
 * A timestamp the way a person says one.
 *
 * Today and yesterday are named rather than dated, because "16:21" with today's
 * date in front of it is a date nobody reads. Seconds are dropped: a person
 * looking at what their agent did this morning is not timing it.
 */
export function whenWords(ts: number | null, now: Date = new Date()): string {
  if (ts === null || !Number.isFinite(ts)) return "";
  const at = new Date(ts * 1000);
  const clock = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  const days = dayNumber(now) - dayNumber(at);
  if (days === 0) return `今天 ${clock}`;
  if (days === 1) return `昨天 ${clock}`;
  return `${at.getMonth() + 1} 月 ${at.getDate()} 日 ${clock}`;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Local midnights, so "yesterday" does not depend on the hour. */
function dayNumber(date: Date): number {
  return Math.floor(
    (date.getTime() - date.getTimezoneOffset() * 60_000) / 86_400_000,
  );
}
